import { useMemo } from 'react';
import {
  HydratedCallRecord,
  NetworkPerformance,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { TaskSource } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import {
  baseUrlV2 as CallIntelBaseUrlV2,
  CallIntelligenceV2,
  CallIntelTypes,
  useGetCallMetadataQuery,
} from '@frontend/api-call-intel';
import { TaskCenterQueries } from '@frontend/api-task-center';
import { getWeaveToken, hasSchemaACL } from '@frontend/auth-helpers';
import { callStatusUtils, useHasCallIntelAclAccess } from '@frontend/call-intel-core';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { CallInsightPanelSource } from '../types';
import { getContactInfo, processTasks } from '../utils/call-insight-panel-util';

type Props = {
  callRecord: HydratedCallRecord;
  isDemoAccount?: boolean;
  source: CallInsightPanelSource;
};

export const useCallInsightPanelData = ({ callRecord, isDemoAccount, source }: Props) => {
  const { t } = useTranslation('call-insight-panel');
  const alert = useAlert();
  const { selectedLocationIds, getLocationName, accessibleLocationData } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const contactInfo = useMemo(() => getContactInfo(callRecord), [callRecord]);
  const isTaskArchivalEnabled = getFeatureFlagValue('task-archival');
  const hasCallIntelAccess = useHasCallIntelAclAccess([Permission.CALL_CLASSIFICATION_READ], {
    locations: [callRecord.locationId ?? ''],
  });
  const weaveToken = getWeaveToken();
  const locationId = callRecord.locationId || selectedLocationIds?.[0] || '';

  // Realtime recording updates lack access-specific statuses, so retain ACL checks as a fallback.
  const hasAnyAccessToCallRecording = useMemo(
    () =>
      selectedLocationIds.some((selectedLocationId) => {
        const location = accessibleLocationData[selectedLocationId];
        const hasPhoneFullAccess =
          location?.locationType === 'unify'
            ? hasSchemaACL(location.groupId, Permission.PHONE_FULL_ACCESS)
            : hasSchemaACL(location?.id, Permission.PHONE_FULL_ACCESS);
        return hasSchemaACL(selectedLocationId, Permission.CALL_RECORDING_READ) || hasPhoneFullAccess;
      }),
    [accessibleLocationData, selectedLocationIds]
  );
  const hasAccessToCallRecording = useMemo(() => {
    const location = accessibleLocationData[locationId];
    const hasPhoneFullAccess =
      location?.locationType === 'unify'
        ? hasSchemaACL(location.groupId, Permission.PHONE_FULL_ACCESS)
        : hasSchemaACL(location?.id, Permission.PHONE_FULL_ACCESS);
    return hasSchemaACL(locationId, Permission.CALL_RECORDING_READ) || hasPhoneFullAccess;
  }, [accessibleLocationData, locationId]);

  const { isTaskCreationEnabled: isTasksEnabled } = CallIntelligenceV2.Hooks.useAggregateTaskSettings({
    locationId,
    isDemo: isDemoAccount,
  });

  const { isFailedCall, isSkippedCall, isProcessingCall, isDeferredCall } = callStatusUtils.getCallFlags(
    callRecord.callIntelInsights
  );

  const callRecordingPath = useMemo(() => {
    if (!callRecord) return '';

    if (isDemoAccount) {
      return `${appConfig.BACKEND_API}/${CallIntelBaseUrlV2}/demo/call-recording?token=${weaveToken}&location_id=${locationId}`;
    }

    return '';
  }, [callRecord, isDemoAccount, locationId, weaveToken]);

  const networkPerformance = useMemo(() => {
    const hasPerformanceIssue = callRecord.networkPerformance === NetworkPerformance.NETWORK_PERFORMANCE_ISSUE_DETECTED;
    const isPerformanceGood = callRecord.networkPerformance === NetworkPerformance.NETWORK_PERFORMANCE_GOOD;
    const shouldDisplay = hasPerformanceIssue || isPerformanceGood;
    return {
      hasPerformanceIssue,
      isPerformanceGood,
      performanceStatus: callRecord.networkPerformance,
      shouldDisplay,
      showQualityAccordion: shouldDisplay,
    };
  }, [callRecord.networkPerformance]);

  const {
    data: callMetadataRes,
    isLoading: isLoadingCallMetadata,
    refetch: refetchCallMetadata,
  } = useGetCallMetadataQuery({
    request: {
      id: callRecord.channelId ?? '',
      locationId: callRecord.locationId ?? '',
      isDemo: isDemoAccount,
      demoMetadata: isDemoAccount
        ? ({
            callRecord,
            locationName: getLocationName(locationId),
            invokedBy:
              source === 'callHistory'
                ? CallIntelTypes.CallMetadataRequestSource.PHONE_EVENTS
                : CallIntelTypes.CallMetadataRequestSource.INVALID_SOURCE,
            isFromContactPanel: source === 'callHistoryContactPanel' || source === 'patientInsightsContactPanel',
          } as CallIntelTypes.GetCallMetadataIO['input']['demoMetadata'] & {
            callRecord: CallIntelTypes.CallRecord;
            isFromContactPanel: boolean;
          }) // callRecord and isFromContactPanel are not in contract but will be used by demo interceptor
        : undefined,
    },
    options: {
      enabled:
        !!callRecord.channelId &&
        !!callRecord.locationId &&
        !!callRecord.callIntelInsights &&
        !isFailedCall &&
        !isSkippedCall &&
        !isProcessingCall &&
        !isDeferredCall,
      onError: () => {
        alert.error(t("Couldn't fetch call metadata. Please try again."));
      },
      staleTime: 1000 * 60 * 5,
    },
  });

  const {
    data: taskDataRes,
    isLoading: isTaskFetching,
    queryKey: taskQueryKey,
  } = TaskCenterQueries.useGetTasksListV2(
    {
      filters: {
        locationId: callRecord?.locationId ? [callRecord.locationId] : [],
        sourceId: callRecord?.channelId ? [callRecord.channelId] : [],
        source: TaskSource.SOURCE_CALL_INTELLIGENCE,
      },
      ...(isTaskArchivalEnabled && {
        includeArchived: false,
      }),
      includeMetadata: true,
      isDemo: isDemoAccount,
      demoMetadata: isDemoAccount
        ? {
            callIntelligence: {
              sourceIdStartTimeMap:
                callRecord.channelId && callRecord.startedAt
                  ? {
                      [callRecord.channelId]: callRecord.startedAt,
                    }
                  : {},
            },
          }
        : undefined,
    },
    {
      cacheTime: 0,
      staleTime: 0,
      enabled:
        !!isTasksEnabled &&
        !!callRecord?.channelId &&
        !!callRecord?.locationId &&
        !isFailedCall &&
        !isSkippedCall &&
        !isProcessingCall &&
        !isDeferredCall &&
        (callRecord.callIntelInsights?.taskCount || callRecord.taskCount || 0) > 0,
      onError: () => {
        alert.error(t('Failed to fetch call tasks'));
      },
    }
  );

  const tasks = useMemo(() => taskDataRes?.pages.flatMap((page) => page.tasks ?? []) ?? [], [taskDataRes?.pages]);

  const callMetadata = useMemo<CallIntelTypes.CallMetadata>(() => {
    const transcript = callMetadataRes?.transcript ?? '';
    return {
      callParticipantsMetadata: {
        caller: callMetadataRes?.callMetadata?.caller ?? '',
        receptionist: callMetadataRes?.callMetadata?.receptionist ?? '',
      },
      rating: callMetadataRes?.rating,
      summary: callMetadataRes?.summary ?? '',
      transcript,
      tasks: processTasks(tasks, transcript),
    };
  }, [callMetadataRes, tasks]);

  return {
    callMetadata,
    callRecordingPath,
    hasAccessToCallRecording,
    hasAnyAccessToCallRecording,
    contactInfo,
    hasCallIntelAccess,
    isLoadingCallMetadata,
    isTaskFetching,
    networkPerformance,
    taskQueryKey,
    isTasksEnabled,
    refetchCallMetadata,
  };
};
